import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../lib/api';

const ImpersonationContext = createContext(null);

const IMPERSONATION_TOKEN_KEY = 'impersonation_token';
const IMPERSONATION_DATA_KEY = 'impersonation_data';

export const ImpersonationProvider = ({ children }) => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationData, setImpersonationData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper function to clear impersonation state
  const clearImpersonation = useCallback(() => {
    localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATION_DATA_KEY);
    setIsImpersonating(false);
    setImpersonationData(null);
  }, []);

  // Initialize from localStorage on mount
  useEffect(() => {
    const initializeImpersonation = async () => {
      const token = localStorage.getItem(IMPERSONATION_TOKEN_KEY);
      const storedData = localStorage.getItem(IMPERSONATION_DATA_KEY);
      
      if (token && storedData) {
        try {
          // Validate the token is still active
          const response = await adminAPI.validateImpersonation(token);
          
          if (response.data.active) {
            setIsImpersonating(true);
            setImpersonationData({
              ...JSON.parse(storedData),
              ...response.data,
              token
            });
          } else {
            // Token expired or invalid, clean up
            clearImpersonation();
          }
        } catch (error) {
          console.error('Failed to validate impersonation:', error);
          clearImpersonation();
        }
      }
      setLoading(false);
    };

    initializeImpersonation();
  }, [clearImpersonation]);

  const startImpersonation = useCallback(async (tenantId, readOnly = false) => {
    try {
      const response = await adminAPI.startImpersonation(tenantId, readOnly);
      const data = response.data;
      
      // Store in localStorage
      localStorage.setItem(IMPERSONATION_TOKEN_KEY, data.token);
      localStorage.setItem(IMPERSONATION_DATA_KEY, JSON.stringify({
        tenant_id: data.tenant_id,
        tenant_name: data.tenant_name,
        read_only: data.read_only,
        expires_at: data.expires_at,
      }));
      
      setIsImpersonating(true);
      setImpersonationData({
        ...data,
        active: true
      });
      
      return { success: true, data };
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to start impersonation' 
      };
    }
  }, []);

  const exitImpersonation = useCallback(async () => {
    const token = localStorage.getItem(IMPERSONATION_TOKEN_KEY);
    
    if (token) {
      try {
        await adminAPI.exitImpersonation(token);
      } catch (error) {
        console.error('Failed to end impersonation on server:', error);
        // Still clear local state even if server call fails
      }
    }
    
    clearImpersonation();
    return { success: true };
  }, [clearImpersonation]);

  const getImpersonationToken = useCallback(() => {
    return localStorage.getItem(IMPERSONATION_TOKEN_KEY);
  }, []);

  const value = {
    isImpersonating,
    impersonationData,
    loading,
    startImpersonation,
    exitImpersonation,
    getImpersonationToken,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

export default ImpersonationContext;
